import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Lets the active tab register a "refresh" handler with the app shell, so the
 * top-right action button in the header refreshes whatever the user is
 * currently looking at.
 *
 * Pattern: each tab page calls `useRegisterRefresh(refetchFn)` on mount; the
 * app shell renders an ActionButton bound to the current refresh handler.
 */
interface PageActionsValue {
  refresh: (() => void) | null;
  setRefresh: (fn: (() => void) | null) => void;
}

const Ctx = createContext<PageActionsValue | null>(null);

export function PageActionsProvider({ children }: { children: ReactNode }) {
  const [refresh, setRefresh] = useState<(() => void) | null>(null);
  const value = useMemo(() => ({ refresh, setRefresh }), [refresh]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePageActions(): PageActionsValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('usePageActions must be used inside PageActionsProvider');
  return ctx;
}

/** Tabs call this to wire up the header refresh button. */
export function useRegisterRefresh(refetch: () => void): void {
  const { setRefresh } = usePageActions();
  useEffect(() => {
    setRefresh(() => refetch);
    return () => setRefresh(null);
  }, [refetch, setRefresh]);
}
